/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UMSS_RADIO_ITEM } from '../radio-fieldset/radio-item.token';

@Component({
  selector: 'umss-radio',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './radio.component.html',
  styleUrls: ['./radio.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssRadioComponent),
      multi: true
    },
    {
      provide: UMSS_RADIO_ITEM,
      useExisting: forwardRef(() => UmssRadioComponent)
    }
  ]
})
export class UmssRadioComponent implements ControlValueAccessor {
  @HostBinding('style.display') display = 'contents';

  /** Content props */
  @Input() value: unknown;
  @Input() name?: string;
  @Input() ariaLabel?: string;

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;

  /** Value prop */
  private _checked: boolean = false;
  @Input() set checked(val: boolean) {
    this._checked = val;
    if (val) {
      this._onChange(this.value);
    }
  }
  get checked(): boolean {
    return this._checked;
  }

  /** Event outputs */
  @Output() checkedChange = new EventEmitter<unknown>();
  @Output() change = new EventEmitter<unknown>();
  @Output() onFocus = new EventEmitter<void>();
  @Output() onBlur = new EventEmitter<void>();

  /** ControlValueAccessor implementation */
  private _onChange: (value: unknown) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: unknown): void {
    this._checked = value === this.value;
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onSelect(): void {
    if (this.disabled) return;

    this._checked = true;
    this._onChange(this.value);
    this._onTouched();
    this.checkedChange.emit(this.value);
    this.change.emit(this.value);
  }

  onFocusEvent(): void {
    if (this.disabled) return;
    this.onFocus.emit();
  }

  onBlurEvent(): void {
    if (this.disabled) return;
    this._onTouched();
    this.onBlur.emit();
  }

  /** Computed properties */
  get computedAriaLabel(): string {
    return this.ariaLabel || `Radio option ${this.value}`;
  }
}
